import { normalizeBoundedPage } from "../lib/forfeoQueryPerformance"; // Lot 4F-F3B
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabaseClient'; 
import {
  MapPin, Star, Share, Heart, Wifi, Coffee, Car,
  CheckCircle, Users, ExternalLink, ShoppingBag,
  Sparkles, Image as ImageIcon, X, Plus, ArrowRight, CreditCard, Loader2, AlertTriangle,
  MessageSquare as MessageSquareText, MessageSquare, ArrowLeft,
  ShieldCheck, Send, ThumbsUp, Clock, Droplets, Wind, Tv, Utensils, Lock, Activity, HelpCircle, Smile // ✅ CORRECTION : AJOUT DE CLOCK ET DES AUTRES ICÔNES
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import ChatSystem from '../components/ChatSystem'; 
import ReserveVirtualTourViewer from '../components/reserve/ReserveVirtualTourViewer';
import { isForfeoReserveVirtualTourEnabled } from '../lib/forfeoReserveVirtualTourClient';
import { isForfeoReserveUserExperienceEnabled } from '../lib/forfeoReserveUserExperienceClient';

// --- TYPES ---
type Business = {
  id: string;
  name: string;
  description: string;
  address: string;
  city: string;
  region: string;
  image_url: string;
  category: string;
  phone: string;
  website: string;
  prp_id?: string | null; 

  full_address?: string | null;
  street_address?: string | null;
  is_hotel_active?: boolean | null;
};

type Room = {
  id: string;
  name: string;
  description: string;
  price_per_night: number;
  capacity_adults: number;
  images: string[];
  image_url?: string;
  amenities?: string[];
};

type Product = {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  stock_quantity: number;
};

type Review = {
  id: string;
  business_id?: string;
  place_id?: string;
  user_id?: string;
  created_at?: string;

  title?: string;
  comment?: string;
  pros?: string;
  cons?: string;

  rating?: number;
  rating_overall?: number;
  overall?: number;

  cleanliness?: number;
  comfort?: number;
  location?: number;
  value?: number;
  rating_cleanliness?: number;
  rating_comfort?: number;
  rating_location?: number;
  rating_value?: number;

  is_verified?: boolean;

  user_name?: string;
  author_name?: string;
};

type HotelDetails = {
  business_id: string;
  highlights?: string[] | null;
  amenities?: Record<string, string[]> | string[] | null;
  dining?: {
    has_restaurant?: boolean;
    restaurants?: Array<{
      name?: string;
      serves?: string[];
      cuisine_type?: string;
      service_style?: string;
      opening_hours?: string;
      notes?: string;
    }>;
  } | null;
  house_rules?: {
    check_in?: string;
    check_out?: string;
    cancellation?: string;
    deposit?: string;
    children?: string;
    age_restriction?: string;
    pets?: string;
    smoking?: string;
    parties?: string;
    quiet_hours?: string;
    other?: string[];
  } | null;
  surroundings?: {
    items?: Array<{
      label: string;
      distance?: string;
      category?: string;
    }>;
    notes?: string;
  } | null;
  traveler_questions?: Array<{ q: string; a: string }> | null;
  faq?: Array<{ q: string; a: string }> | null;
  rules?: {
    check_in?: string;
    check_out?: string;
    pets_allowed?: boolean;
    smoking_allowed?: boolean;
    parties_allowed?: boolean;
    cancellation?: string;
    deposit?: string;
    children?: string;
    quiet_hours?: string;
    other?: string[];
  } | null;
  updated_at?: string | null;
};

type Post = {
  id: string;
  content: string;
  image_url: string;
  likes_count: number;
  created_at: string;
  comments: any[];
};

type ForfyPlanStop = {
  title: string;
  subtitle?: string;
  reason?: string;
  mapsQuery?: string;
  duration?: string;
};

type ForfyPlanBlock = {
  label: string;
  time: string;
  title: string;
  summary: string;
  stops: ForfyPlanStop[];
};

type ForfyPlan = {
  title: string;
  intro: string;
  bestFor: string[];
  blocks: ForfyPlanBlock[];
  tips: string[];
  disclaimers?: string[];
};

// --- HELPERS ---
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

const getAmenityIcon = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes('wifi')) return <Wifi size={14} />;
  if (n.includes('parking') || n.includes('stationnement')) return <Car size={14} />;
  if (n.includes('café') || n.includes('petit-déj')) return <Coffee size={14} />;
  if (n.includes('piscine') || n.includes('bain')) return <Droplets size={14} />;
  if (n.includes('clim') || n.includes('air')) return <Wind size={14} />;
  if (n.includes('tv') || n.includes('télé')) return <Tv size={14} />;
  if (n.includes('cuisine') || n.includes('repas')) return <Utensils size={14} />;
  if (n.includes('sport') || n.includes('gym')) return <Activity size={14} />; 
  if (n.includes('sécurité') || n.includes('coffre')) return <Lock size={14} />;
  return <CheckCircle size={14} />;
};

const REVIEWS_TABLE_CANDIDATES = ['place_reviews', 'reviews', 'business_reviews'];

const PLACE_REVIEWS_PUBLIC_COLUMNS =
  'id, created_at, business_id, place_id, author_name, rating, comment';
const HOTEL_DETAILS_TABLE_CANDIDATES = [
  'business_hotel_details',
  'hotel_details_public',
  'business_hotel_details_public',
  'hotel_details'
];

const scoreLabel = (rating: number) => {
  if (rating >= 9) return 'Fabuleux';
  if (rating >= 8) return 'Très bien';
  if (rating >= 7) return 'Bien';
  if (rating >= 6) return 'Correct';
  return 'Passable';
};

const googleMapsSearchUrl = (query: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const googleMapsDirectionsUrl = (originLat: number, originLng: number, destinationQuery: string) =>
  `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${encodeURIComponent(
    destinationQuery
  )}&travelmode=driving`;


const truthyText = (value?: string | null) => {
  if (!value) return null;
  const n = value.toLowerCase();
  if (n.includes('interdit') || n.includes('non') || n.includes('aucun')) return false;
  if (n.includes('autor') || n.includes('oui') || n.includes('accept') || n.includes('permis')) return true;
  return null;
};

const getReviewerName = (review: Review) =>
  review.author_name || review.user_name || 'Voyageur';

const getReviewStars = (review: Review) => {
  const raw =
    review.rating_overall ??
    review.overall ??
    review.rating ??
    null;

  const normalized = typeof raw === 'number'
    ? raw <= 5 ? raw : raw / 2
    : 0;

  return clamp(Math.round(normalized || 0), 0, 5);
};


const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const coerceForfyPlan = (raw: any, fallbackTitle: string): ForfyPlan | null => {
  const source = raw?.plan || raw?.itinerary || raw?.result || raw?.data || raw;
  if (!source) return null;

  if (typeof source === 'string') {
    return {
      title: fallbackTitle,
      intro: source,
      bestFor: [],
      blocks: [],
      tips: [],
      disclaimers: [],
    };
  }

  const blocksSource = Array.isArray(source.blocks)
    ? source.blocks
    : Array.isArray(source.sections)
      ? source.sections
      : [];

  const blocks: ForfyPlanBlock[] = blocksSource
    .map((block: any, index: number) => {
      const stopsSource = Array.isArray(block?.stops)
        ? block.stops
        : Array.isArray(block?.items)
          ? block.items
          : [];

      return {
        label: String(block?.label || block?.moment || `Étape ${index + 1}`),
        time: String(block?.time || block?.window || ''),
        title: String(block?.title || block?.headline || `Moment ${index + 1}`),
        summary: String(block?.summary || block?.description || ''),
        stops: stopsSource
          .map((stop: any) => ({
            title: String(stop?.title || stop?.name || 'Arrêt'),
            subtitle: stop?.subtitle ? String(stop.subtitle) : undefined,
            reason: stop?.reason ? String(stop.reason) : stop?.description ? String(stop.description) : undefined,
            mapsQuery: stop?.mapsQuery ? String(stop.mapsQuery) : stop?.query ? String(stop.query) : stop?.title ? String(stop.title) : undefined,
            duration: stop?.duration ? String(stop.duration) : undefined,
          }))
          .filter((stop: ForfyPlanStop) => stop.title),
      };
    })
    .filter((block: ForfyPlanBlock) => block.title);

  return {
    title: String(source?.title || fallbackTitle),
    intro: String(source?.intro || source?.summary || source?.description || ''),
    bestFor: toArray(source?.bestFor || source?.best_for || source?.tags),
    blocks,
    tips: toArray(source?.tips || source?.travelTips || source?.practicalTips),
    disclaimers: toArray(source?.disclaimers || source?.notes),
  };
};

function BarRow({ label, value }: { label: string; value: number }) {
  const pct = clamp((value / 10) * 100, 0, 100);
  return (
    <div className="grid grid-cols-[minmax(110px,150px)_minmax(0,1fr)_42px] items-center gap-3 sm:gap-4">
      <div className="text-sm font-bold leading-tight text-slate-700">{label}</div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-slate-900 via-blue-700 to-indigo-600"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-sm font-black text-slate-900 text-center tabular-nums">
        {value.toFixed(1)}
      </div>
    </div>
  );
}

function AccordionItem({
  q, a, open, onToggle
}: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors duration-300"
      >
        <span className="font-bold text-slate-900 text-[15px]">{q}</span>
        <span className={`transition-transform duration-300 ${open ? 'rotate-90' : ''}`}>
          <ArrowRight size={18} className="text-slate-400" />
        </span>
      </button>
      {open && (
        <div className="p-5 pt-0 text-[15px] text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
          {a}
        </div>
      )}
    </div>
  );
}

export default function BusinessProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const reserveUserExperienceEnabled = isForfeoReserveUserExperienceEnabled();

  const [business, setBusiness] = useState<Business | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [posts, setPosts] = useState<Post[]>([]); 
  const [loading, setLoading] = useState(true);

  const [hotelDetails, setHotelDetails] = useState<HotelDetails | null>(null);
  const [hotelDetailsLoading, setHotelDetailsLoading] = useState(false);

  const [aroundOpen, setAroundOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoErr, setGeoErr] = useState<string | null>(null);

  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [showGallery, setShowGallery] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const [isDateAvailable, setIsDateAvailable] = useState<boolean | null>(null);
  const [availabilityMessage, setAvailabilityMessage] = useState("");

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [activeReviewsTable, setActiveReviewsTable] = useState<string | null>(null);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const [rClean, setRClean] = useState<number>(9);
  const [rComfort, setRComfort] = useState<number>(9);
  const [rLocation, setRLocation] = useState<number>(9);
  const [rValue, setRValue] = useState<number>(9.1);
  const [rTitle, setRTitle] = useState<string>('');
  const [rPros, setRPros] = useState<string>('');
  const [rCons, setRCons] = useState<string>('');
  const [rComment, setRComment] = useState<string>('');

  const [userHasConfirmedReservation, setUserHasConfirmedReservation] = useState<boolean>(false);

  const [openQa, setOpenQa] = useState<number | null>(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // États Chat & Social
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());

  const [showItineraryPlanner, setShowItineraryPlanner] = useState(false);
  const [itineraryLoading, setItineraryLoading] = useState(false);
  const [itineraryError, setItineraryError] = useState<string | null>(null);
  const [itinerarySource, setItinerarySource] = useState<'openai' | 'api' | 'fallback' | null>(null);
  const [itineraryModelName, setItineraryModelName] = useState<string | null>(null);
  const [itineraryWarning, setItineraryWarning] = useState<string | null>(null);
  const [itineraryEndpointLabel, setItineraryEndpointLabel] = useState<string | null>(null);
  const [itineraryMoment, setItineraryMoment] = useState<'morning' | 'afternoon' | 'evening' | 'full'>('full');
  const [itineraryStyle, setItineraryStyle] = useState<'relax' | 'foodie' | 'family' | 'romance' | 'discovery'>('discovery');
  const [itineraryBudget, setItineraryBudget] = useState<'soft' | 'medium' | 'premium'>('medium');
  const [itineraryTravelMode, setItineraryTravelMode] = useState<'drive' | 'walk'>('drive');
  const [itineraryPlan, setItineraryPlan] = useState<ForfyPlan | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];

  // --- DÉTECTION DU PARAMÈTRE URL POUR LE CHAT ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('chat') === 'true') {
      const timer = setTimeout(() => setIsChatOpen(true), 1200); 
      window.history.replaceState({}, '', location.pathname);
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  useEffect(() => {
    if (!showGallery && !showReviewModal && !aroundOpen && !isChatOpen && !showItineraryPlanner) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showGallery, showReviewModal, aroundOpen, isChatOpen, showItineraryPlanner]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) setCurrentUserId(session.user.id);

        const { data: busData, error: busError } = await supabase
          .from('businesses')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (busError) throw busError;
        if (!busData) {
          console.error("Business introuvable:", id);
          return;
        }
        setBusiness(busData);

        const { data: postsData } = await supabase
          .from('posts')
          .select('*, comments:post_comments(id, content, created_at)')
          .eq('business_id', id)
          .order('created_at', { ascending: false })
          .limit(normalizeBoundedPage({ pageSize: 24 }).pageSize);
        if (postsData) setPosts(postsData);

        const { data: roomData } = await supabase
          .from('accommodations')
          .select('*')
          .eq('business_id', id)
          .eq('is_available', true);

        const availableRooms = roomData || [];
        setRooms(availableRooms);

        const inboundParams = new URLSearchParams(location.search);
        const preferredRoomId = inboundParams.get('legacyRoomId');
        const preferredRoom = preferredRoomId
          ? availableRooms.find((room: Room) => room.id === preferredRoomId) || null
          : null;
        setSelectedRoom(preferredRoom || availableRooms[0] || null);

        const inboundCheckIn = inboundParams.get('check_in') || inboundParams.get('date');
        const inboundCheckOut = inboundParams.get('check_out') || inboundParams.get('endDate');
        const inboundPartySize = Number(inboundParams.get('partySize') || '');
        if (inboundCheckIn && /^\d{4}-\d{2}-\d{2}$/.test(inboundCheckIn)) setCheckIn(inboundCheckIn);
        if (inboundCheckOut && /^\d{4}-\d{2}-\d{2}$/.test(inboundCheckOut)) setCheckOut(inboundCheckOut);
        if (Number.isFinite(inboundPartySize) && inboundPartySize >= 1) {
          setGuests(Math.min(20, Math.floor(inboundPartySize)));
        }

        const { data: prodData } = await supabase
          .from('shop_products')
          .select('*')
          .eq('business_id', id)
          .eq('is_active', true);
        setProducts(prodData || []);

      } catch (err) {
        console.error("Erreur chargement profil:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const savedLikes = JSON.parse(localStorage.getItem('forfeo_liked_posts') || '[]');
    setLikedPosts(new Set(savedLikes));
    window.scrollTo(0, 0);
  }, [id, location.search]);

  useEffect(() => {
    const fetchHotelDetails = async () => {
      if (!id) return;
      setHotelDetailsLoading(true);
      try {
        let found: HotelDetails | null = null;

        for (const t of HOTEL_DETAILS_TABLE_CANDIDATES) {
          try {
            const res = await supabase
              .from(t)
              .select('*')
              .eq('business_id', id)
              .maybeSingle();

            if ((res as any)?.status === 404) continue;

            if (!res.error && res.data) {
              found = res.data as HotelDetails;
              break;
            }
          } catch { }
        }

        setHotelDetails(found);
      } finally {
        setHotelDetailsLoading(false);
      }
    };

    fetchHotelDetails();
  }, [id]);

  useEffect(() => {
    const checkAvailability = async () => {
      setIsDateAvailable(null);
      setAvailabilityMessage("");

      if (reserveUserExperienceEnabled) return;
      if (!checkIn || !checkOut || !selectedRoom) return;

      if (checkIn < todayStr) {
        setIsDateAvailable(false);
        setAvailabilityMessage("Date passée impossible.");
        return;
      }
      if (checkIn >= checkOut) {
        setIsDateAvailable(false);
        setAvailabilityMessage("Départ doit être après arrivée.");
        return;
      }

      try {
        const { count, error } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('accommodation_id', selectedRoom.id)
          .in('status', ['confirmed', 'blocked'])
          .or(`check_in_date.lt.${checkOut},check_out_date.gt.${checkIn}`);

        if (error) throw error;

        if (count && count > 0) {
          setIsDateAvailable(false);
          setAvailabilityMessage("Dates indisponibles (Déjà réservé ou Fermé).");
        } else {
          setIsDateAvailable(true);
          setAvailabilityMessage("Dates disponibles !");
        }
      } catch (err) {
        console.error("Erreur dispo:", err);
        setIsDateAvailable(false);
        setAvailabilityMessage("Erreur de vérification.");
      }
    };
    checkAvailability();
  }, [checkIn, checkOut, selectedRoom, todayStr, reserveUserExperienceEnabled]);

  const totalPrice = useMemo(() => {
    if (!checkIn || !checkOut || !selectedRoom) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights * selectedRoom.price_per_night : 0;
  }, [checkIn, checkOut, selectedRoom]);

  const totalNights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  const selectAndScroll = (room: Room) => {
    setSelectedRoom(room);
    setTimeout(() => {
      const bookingPanel = document.getElementById('booking-panel');
      if (bookingPanel) {
        bookingPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        bookingPanel.classList.add('ring-4', 'ring-blue-200');
        setTimeout(() => bookingPanel.classList.remove('ring-4', 'ring-blue-200'), 1000);
      }
    }, 100);
  };

  const handleBooking = () => {
    if (!business) return;

    const params = new URLSearchParams();
    if (checkIn) params.set('date', checkIn);
    if (checkOut) params.set('endDate', checkOut);
    if (guests > 0) params.set('partySize', String(guests));
    if (selectedRoom?.id) params.set('legacyRoomId', selectedRoom.id);

    setBookingLoading(true);
    navigate(`/booking/${business.id}${params.size ? `?${params.toString()}` : ''}`);
  };

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      source_table: 'shop_products',
      source_id: product.id,
      name: product.name,
      price: product.price,
      image: product.image_url,
      type: 'product',
      business_id: business?.id
    });
  };

  const handleShare = async () => {
    if (!business) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: business.name,
          text: `Découvrez ${business.name} sur Forfeo !`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier !');
    }
  };

  const handleLikePost = async (postId: string) => {
    const isAlreadyLiked = likedPosts.has(postId);
    const amount = isAlreadyLiked ? -1 : 1;
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + amount } : p));
    const newLikes = new Set(likedPosts);
    isAlreadyLiked ? newLikes.delete(postId) : newLikes.add(postId);
    setLikedPosts(newLikes);
    localStorage.setItem('forfeo_liked_posts', JSON.stringify(Array.from(newLikes)));
    await supabase.rpc('update_likes', { p_post_id: postId, p_amount: amount });
  };

  const allImages = useMemo(() => {
    if (!business) return [];
    let imgs = business.image_url ? [business.image_url] : [];
    rooms.forEach(r => {
      if (r.images && r.images.length > 0) imgs.push(...r.images);
      else if (r.image_url) imgs.push(r.image_url);
    });
    return Array.from(new Set(imgs)).filter(url => url && url.length > 5);
  }, [business, rooms]);

  const normalizeRating = (rev: Review) => {
    const overall =
      rev.rating_overall ??
      rev.overall ??
      rev.rating ??
      null;

    if (typeof overall === 'number') return overall;

    const c = rev.cleanliness ?? rev.rating_cleanliness;
    const co = rev.comfort ?? rev.rating_comfort;
    const l = rev.location ?? rev.rating_location;
    const v = rev.value ?? rev.rating_value;
    const arr = [c, co, l, v].filter(x => typeof x === 'number') as number[];
    if (arr.length === 0) return 0;
    return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
  };

  const averageRating = useMemo(() => {
    if (!reviews.length) return 0;
    const vals = reviews.map(normalizeRating).filter(n => typeof n === 'number');
    if (!vals.length) return 0;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  }, [reviews]);

  const categoryAverages = useMemo(() => {
    const arrClean: number[] = [];
    const arrComfort: number[] = [];
    const arrLocation: number[] = [];
    const arrValue: number[] = [];

    for (const r of reviews) {
      const c = (r.cleanliness ?? r.rating_cleanliness);
      const co = (r.comfort ?? r.rating_comfort);
      const l = (r.location ?? r.rating_location);
      const v = (r.value ?? r.rating_value);

      if (typeof c === 'number') arrClean.push(c);
      if (typeof co === 'number') arrComfort.push(co);
      if (typeof l === 'number') arrLocation.push(l);
      if (typeof v === 'number') arrValue.push(v);
    }

    const avg = (xs: number[], fallback: number) =>
      xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : fallback;

    const base = averageRating > 0 ? averageRating : 8.2;

    return {
      personnel: clamp(base + 0.2, 0, 10),
      equipements: clamp(base - 0.3, 0, 10),
      proprete: avg(arrClean, clamp(base - 0.1, 0, 10)),
      confort: avg(arrComfort, clamp(base - 0.2, 0, 10)),
      qp: avg(arrValue, clamp(base - 0.4, 0, 10)),
      geo: avg(arrLocation, clamp(base + 0.5, 0, 10)),
      wifi: clamp(base - 0.1, 0, 10),
    };
  }, [reviews, averageRating]);

  const checkUserVerifiedNow = async (): Promise<boolean> => {
    if (!id) return false;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserHasConfirmedReservation(false);
        return false;
      }

      const { count, error } = await supabase
        .from('reservations')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', id)
        .eq('user_id', user.id)
        .in('status', ['confirmed']);

      if (error) {
        setUserHasConfirmedReservation(false);
        return false;
      }

      const ok = !!count && count > 0;
      setUserHasConfirmedReservation(ok);
      return ok;
    } catch (e) {
      setUserHasConfirmedReservation(false);
      return false;
    }
  };

  const fetchReviews = async () => {
    if (!id) return;
    setReviewsLoading(true);

    const orderOpt = { ascending: false as const };

    const trySelect = async (table: string) => {
      const selectedColumns =
        table === 'place_reviews'
          ? PLACE_REVIEWS_PUBLIC_COLUMNS
          : '*';

      let res: any = await supabase
        .from(table)
        .select(selectedColumns)
        .eq('business_id', id)
        .order('created_at', orderOpt);

      if (!res.error) return { table, data: (res.data as Review[]) || [] };
      if (res.status === 404) return null;

      res = await supabase
        .from(table)
        .select(selectedColumns)
        .eq('place_id', id)
        .order('created_at', orderOpt);

      if (!res.error) return { table, data: (res.data as Review[]) || [] };
      if (res.status === 404) return null;

      return null;
    };

    try {
      let chosen: { table: string; data: Review[] } | null = null;
      for (const t of REVIEWS_TABLE_CANDIDATES) {
        const out = await trySelect(t);
        if (out) { chosen = out; break; }
      }

      setActiveReviewsTable(chosen?.table || null);
      setReviews(chosen?.data || []);
    } catch (e) {
      setActiveReviewsTable(null);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    checkUserVerifiedNow();
  }, [id]);

  const openReviewModal = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert("Connecte-toi pour laisser un avis.");
      navigate('/club');
      return;
    }
    await checkUserVerifiedNow();
    setShowReviewModal(true);
  };

  const extractMissingColumn = (msg: string) => {
    const m1 = msg.match(/column "?([a-zA-Z0-9_]+)"? of relation/i);
    if (m1?.[1]) return m1[1];
    const m2 = msg.match(/Could not find the '([a-zA-Z0-9_]+)' column/i);
    if (m2?.[1]) return m2[1];
    return null;
  };

  const isRlsError = (msg: string) => {
    const m = msg.toLowerCase();
    return m.includes('row-level security') || m.includes('violates row-level security') || m.includes('new row violates');
  };

  const submitReview = async () => {
    if (!id) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert("Connecte-toi pour laisser un avis.");
      navigate('/club');
      return;
    }

    if (!rTitle.trim() || !rComment.trim()) {
      alert("Ajoute au moins un titre et un commentaire.");
      return;
    }

    setReviewSubmitting(true);

    try {
      const isVerified = await checkUserVerifiedNow();

      const basePayload: any = {
        business_id: id,
        place_id: id,
        user_id: user.id,
        title: rTitle.trim(),
        pros: rPros.trim(),
        cons: rCons.trim(),
        comment: rComment.trim(),
        cleanliness: rClean,
        comfort: rComfort,
        location: rLocation,
        value: rValue,
        rating_overall: Math.round(((rClean + rComfort + rLocation + rValue) / 4) * 10) / 10,
        is_verified: isVerified,
        user_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Voyageur',
      };

      const variants: any[] = [
        { ...basePayload },
        {
          business_id: id,
          user_id: user.id,
          rating: Math.round(((rClean + rComfort + rLocation + rValue) / 4) * 10) / 10,
          comment: rComment.trim(),
          title: rTitle.trim(),
          user_name: basePayload.user_name,
        },
        {
          business_id: id,
          user_id: user.id,
          rating: Math.round(((rClean + rComfort + rLocation + rValue) / 4) * 10) / 10,
          comment: rComment.trim(),
        }
      ];

      const tablesToTry = [
        ...(activeReviewsTable ? [activeReviewsTable] : []),
        ...REVIEWS_TABLE_CANDIDATES
      ].filter((v, i, arr) => arr.indexOf(v) === i);

      let inserted = false;
      let lastErr: any = null;

      for (const t of tablesToTry) {
        for (const variant of variants) {
          let payload = { ...variant };
          for (let attempt = 0; attempt < 8; attempt++) {
            const res = await supabase
              .from(t)
              .insert(payload);
            if (!res.error) {
              inserted = true;
              setActiveReviewsTable(t);
              break;
            }

            lastErr = res.error;
            const msg = (res.error as any)?.message || '';

            if ((res as any)?.status === 404) break;
            if (isRlsError(msg)) {
              throw new Error("RLS_BLOCK");
            }

            const missingCol = extractMissingColumn(msg);
            if (missingCol && payload.hasOwnProperty(missingCol)) {
              const next = { ...payload };
              delete next[missingCol];
              payload = next;
              continue;
            }

            if (msg.toLowerCase().includes('is_verified') && payload.is_verified !== undefined) {
              const next = { ...payload };
              delete next.is_verified;
              payload = next;
              continue;
            }
            break;
          }
          if (inserted) break;
        }
        if (inserted) break;
      }

      if (!inserted) {
        console.error("Erreur insert avis:", lastErr);
        const msg = (lastErr as any)?.message || '';
        alert("Impossible d'envoyer l'avis. Détail: " + msg);
        return;
      }

      setShowReviewModal(false);
      await fetchReviews();
      alert("Merci ! Ton avis a été publié" + (isVerified ? " (Séjour vérifié ✅)" : " ✅"));

    } catch (e: any) {
      if (e?.message === "RLS_BLOCK") {
        alert("Impossible d'envoyer l'avis: RLS bloque l'insert. Ajoute une policy INSERT sur ta table d'avis.");
        return;
      }
      alert("Erreur : " + (e?.message || "Inconnue"));
    } finally {
      setReviewSubmitting(false);
    }
  };

  const placeQuery = useMemo(() => {
    if (!business) return '';
    const parts = [
      business.name,
      business.full_address || business.address,
      business.city,
      business.region
    ].filter(Boolean).map(String);
    return parts.join(' ').trim();
  }, [business]);

  const openDirectionsFromMe = async () => {
    setGeoErr(null);

    if (!placeQuery) {
      setGeoErr("Adresse introuvable (nom/ville/region manquants).");
      return;
    }
    if (!navigator?.geolocation) {
      setGeoErr("La géolocalisation n’est pas disponible sur cet appareil.");
      return;
    }

    setGeoLoading(true);
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const url = googleMapsDirectionsUrl(lat, lng, placeQuery);
          window.open(url, '_blank', 'noreferrer');
          setGeoLoading(false);
        },
        () => {
          setGeoErr("Impossible d’obtenir ta position (permission refusée ou GPS off).");
          setGeoLoading(false);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } catch {
      setGeoErr("Impossible d’utiliser la géolocalisation.");
      setGeoLoading(false);
    }
  };

  const aroundSuggestions = useMemo(() => {
    const base = placeQuery || (business?.region ? `${business.region} Québec` : 'Québec');
    return [
      { label: 'Restaurants populaires', q: `restaurants populaires près de ${base}` },
      { label: 'Brunch / café', q: `brunch café près de ${base}` },
      { label: 'Attractions', q: `attractions près de ${base}` },
      { label: 'Stationnement', q: `stationnement près de ${base}` },
      { label: 'Pharmacie', q: `pharmacie près de ${base}` },
      { label: 'Épicerie', q: `épicerie près de ${base}` },
    ];
  }, [placeQuery, business?.region]);

  const forfyApiCandidates = useMemo(() => {
    const env = (import.meta as any).env || {};
    const apiUrl = env.VITE_API_URL ? String(env.VITE_API_URL).replace(/\/$/, '') : '';
    const supabaseUrl = env.VITE_SUPABASE_URL ? String(env.VITE_SUPABASE_URL).replace(/\/$/, '') : '';
    const directUrl = env.VITE_FORFY_AI_URL ? String(env.VITE_FORFY_AI_URL).replace(/\/$/, '') : '';
    return [directUrl, apiUrl ? `${apiUrl}/ai/forfy-itinerary` : '', supabaseUrl ? `${supabaseUrl}/functions/v1/forfy-itinerary` : ''].filter(Boolean);
  }, []);

  const buildLocalForfyPlan = (): ForfyPlan => {
    const placeName = business?.name || 'cet établissement';
    const baseLocation = [business?.city, business?.region].filter(Boolean).join(', ') || 'Québec';
    const nearby = (surroundings?.items || []).slice(0, 4);
    const foodSpot = dining?.restaurants?.[0]?.name || `Pause gourmande près de ${placeName}`;
    const firstNearby = nearby[0]?.label || 'Découverte locale';
    const secondNearby = nearby[1]?.label || aroundSuggestions[0]?.label || 'Adresse populaire';
    const thirdNearby = nearby[2]?.label || aroundSuggestions[2]?.label || 'Attraction locale';
    const vibeLabel = ({
      relax: 'détente',
      foodie: 'gourmand',
      family: 'famille',
      romance: 'romantique',
      discovery: 'découverte',
    } as Record<string, string>)[itineraryStyle] || 'découverte';
    const budgetLabel = ({
      soft: 'budget léger',
      medium: 'budget équilibré',
      premium: 'budget premium',
    } as Record<string, string>)[itineraryBudget] || 'budget équilibré';

    const baseBlocks: ForfyPlanBlock[] = [
      {
        label: 'Départ',
        time: itineraryMoment === 'evening' ? '17:00' : itineraryMoment === 'afternoon' ? '13:00' : '09:00',
        title: `Commencer par ${placeName}`,
        summary: `Prends quelques minutes pour t’installer et lancer une journée ${vibeLabel} autour de ${baseLocation}.`,
        stops: [
          {
            title: placeName,
            subtitle: 'Point de départ',
            reason: `Excellent point de départ pour rayonner autour de ${baseLocation}.`,
            mapsQuery: placeQuery || `${placeName} ${baseLocation}`,
            duration: '30 à 45 min',
          },
          {
            title: firstNearby,
            subtitle: 'Premier arrêt conseillé',
            reason: 'Facile à rejoindre pour se mettre dans l’ambiance sans perdre de temps.',
            mapsQuery: `${firstNearby} ${baseLocation}`,
            duration: itineraryTravelMode === 'walk' ? '10 à 20 min' : '5 à 10 min',
          },
        ],
      },
      {
        label: 'Moment signature',
        time: itineraryMoment === 'evening' ? '18:30' : itineraryMoment === 'afternoon' ? '15:00' : '11:30',
        title: itineraryStyle === 'foodie' ? 'Parenthèse gourmande' : itineraryStyle === 'family' ? 'Activité simple et fun' : 'Stop coup de cœur',
        summary: `Plan pensé par Forfy pour un rythme ${itineraryTravelMode === 'walk' ? 'à pied' : 'fluide en voiture'} avec un ${budgetLabel}.`,
        stops: [
          {
            title: foodSpot,
            subtitle: itineraryStyle === 'foodie' ? 'À prioriser' : 'Pause recommandée',
            reason: itineraryStyle === 'foodie'
              ? 'Le moment idéal pour tester une bonne adresse ou un brunch local.'
              : 'Ajoute une vraie respiration dans le parcours sans alourdir l’itinéraire.',
            mapsQuery: `${foodSpot} ${baseLocation}`,
            duration: '60 à 90 min',
          },
          {
            title: secondNearby,
            subtitle: 'Option flexible',
            reason: 'Forfy garde une étape facilement remplaçable selon la météo, le temps et l’énergie du moment.',
            mapsQuery: `${secondNearby} ${baseLocation}`,
            duration: '30 à 60 min',
          },
        ],
      },
      {
        label: 'Finale',
        time: itineraryMoment === 'evening' ? '20:30' : itineraryMoment === 'afternoon' ? '17:00' : '16:30',
        title: itineraryStyle === 'relax' ? 'Retour tout en douceur' : 'Dernière touche locale',
        summary: `On termine avec une étape qui garde l’effet “wow” sans compliquer la logistique.`,
        stops: [
          {
            title: thirdNearby,
            subtitle: 'Dernière idée forte',
            reason: 'Bon arrêt pour une photo, une visite express ou un souvenir du quartier.',
            mapsQuery: `${thirdNearby} ${baseLocation}`,
            duration: '30 à 75 min',
          },
          {
            title: itineraryStyle === 'relax' ? 'Retour détente à l’établissement' : 'Retour ou souper libre',
            subtitle: 'Boucle finale',
            reason: `Boucle logique pour conclure près de ${placeName} et garder la soirée simple.`,
            mapsQuery: placeQuery || `${placeName} ${baseLocation}`,
            duration: 'Libre',
          },
        ],
      },
    ];

    const blocks = itineraryMoment === 'full'
      ? baseBlocks
      : baseBlocks.slice(0, itineraryMoment === 'morning' ? 2 : 3);

    return {
      title: `Itinéraire Forfy IA — ${vibeLabel} autour de ${placeName}`,
      intro: `Voici un parcours intelligent construit à partir du profil de ${placeName}, des points d’intérêt à proximité et du style “${vibeLabel}”. Dès que ta route OpenAI sécurisée est branchée, Forfy pourra enrichir ce plan avec une logique encore plus fine et contextuelle.`,
      bestFor: [vibeLabel, budgetLabel, itineraryTravelMode === 'walk' ? 'balade urbaine' : 'déplacements faciles'],
      blocks,
      tips: [
        'Active Google Maps pour valider l’achalandage et les horaires en temps réel.',
        itineraryTravelMode === 'walk' ? 'Prévois des chaussures confortables et une marge météo.' : 'Repère le stationnement avant les heures de pointe.',
        'Garde une étape flexible pour t’adapter à la météo ou à l’attente.',
      ],
      disclaimers: ['Ce parcours reste dynamique et peut être optimisé encore plus finement avec une route OpenAI sécurisée côté serveur.'],
    };
  };

  const generateForfyItinerary = async () => {
    if (!business) return;

    setItineraryLoading(true);
    setItineraryError(null);
    setItineraryWarning(null);
    setItineraryModelName(null);
    setItineraryEndpointLabel(null);

    const payload = {
      business: {
        id: business.id,
        name: business.name,
        city: business.city,
        region: business.region,
        address: business.full_address || business.address || '',
        category: business.category,
        description: business.description,
      },
      placeQuery,
      hotelDetails,
      surroundings: surroundings?.items || [],
      aroundSuggestions,
      preferences: {
        moment: itineraryMoment,
        style: itineraryStyle,
        budget: itineraryBudget,
        travelMode: itineraryTravelMode,
      },
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const anonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

      let livePlan: ForfyPlan | null = null;
      let resolvedSource: 'openai' | 'api' | 'fallback' | null = null;
      let resolvedModel: string | null = null;
      let resolvedWarning: string | null = null;
      let resolvedEndpoint: string | null = null;

      for (const url of forfyApiCandidates) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(session?.access_token || anonKey
                ? { Authorization: `Bearer ${session?.access_token || anonKey}` }
                : {}),
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) continue;

          const data = await response.json().catch(() => null);
          const parsed = coerceForfyPlan(data, `Itinéraire Forfy IA — ${business.name}`);
          if (parsed) {
            livePlan = parsed;
            resolvedSource =
              data?.source === 'openai'
                ? 'openai'
                : data?.source === 'fallback'
                  ? 'fallback'
                  : 'api';
            resolvedModel = typeof data?.model === 'string' ? data.model : null;
            resolvedWarning = typeof data?.warning === 'string' ? data.warning : null;
            resolvedEndpoint = url;
            break;
          }
        } catch {
          // On tente l'endpoint suivant
        }
      }

      if (livePlan) {
        setItineraryPlan(livePlan);
        setItinerarySource(resolvedSource || 'api');
        setItineraryModelName(resolvedModel);
        setItineraryWarning(resolvedWarning);
        setItineraryEndpointLabel(
          resolvedEndpoint
            ? resolvedEndpoint.replace(/^https?:\/\//, '').replace(/\/functions\/v1\//, '/').replace(/\/ai\//, '/')
            : null
        );
      } else {
        setItineraryPlan(buildLocalForfyPlan());
        setItinerarySource('fallback');
        setItineraryWarning('Aucune route IA disponible pour le moment. Forfy a généré un plan local premium.');
      }
    } catch (err: any) {
      setItineraryPlan(buildLocalForfyPlan());
      setItinerarySource('fallback');
      setItineraryError(err?.message || 'Impossible de générer l’itinéraire pour le moment.');
      setItineraryWarning('Bascule automatique sur le moteur local Forfy.');
    } finally {
      setItineraryLoading(false);
    }
  };

  const openForfyPlanner = async () => {
    setAroundOpen(false);
    setShowItineraryPlanner(true);
    if (!itineraryPlan && !itineraryLoading) {
      await generateForfyItinerary();
    }
  };

  const defaultFaq = useMemo(() => {
    const hotel = business?.name || "l’établissement";
    const city = business?.region || "Québec";

    const checkInRule = hotelDetails?.house_rules?.check_in || "À confirmer par l'hôtelier";
    const checkOutRule = hotelDetails?.house_rules?.check_out || "À confirmer par l'hôtelier";

    const hasResto = !!(hotelDetails?.dining?.has_restaurant || (hotelDetails?.dining?.restaurants?.length || 0) > 0);

    return [
      {
        q: `L'établissement ${hotel} dispose-t-il d'une piscine ?`,
        a: `Cela dépend des équipements configurés par l’hôtelier. Consulte la section “Équipements de l’établissement”.`
      },
      {
        q: `Quel type d'hébergement puis-je réserver à l'établissement ${hotel} ?`,
        a: `Tu peux réserver les chambres/offres affichées ci-dessus selon tes dates et la disponibilité.`
      },
      {
        q: `Quels sont les horaires d'arrivée et de départ à l'établissement ${hotel} ?`,
        a: `Arrivée : ${checkInRule}. Départ : ${checkOutRule}.`
      },
      {
        q: `Quel est le tarif d'un séjour à l'établissement ${hotel} ?`,
        a: `Le tarif varie selon la date et la disponibilité. Sélectionne tes dates pour voir le prix total.`
      },
      {
        q: `L'établissement ${hotel} dispose-t-il un restaurant sur place ?`,
        a: hasResto
          ? `Oui, un restaurant sur place est indiqué. Voir la section “Restauration”.`
          : `Aucun restaurant sur place n’est indiqué pour le moment (l’hôtelier pourra compléter).`
      },
      {
        q: `Quelles activités l'établissement ${hotel} propose-t-il ?`,
        a: `Regarde la section “Environs de l’hôtel”, ou utilise “Autour de moi (IA)” pour des idées rapides.`
      },
      {
        q: `L'établissement ${hotel} est-il proche du centre (${city}) ?`,
        a: `Ça dépend du point exact. Utilise “Itinéraire depuis moi” ou ouvre Google Maps pour estimer le temps.`
      },
      {
        q: `L'établissement ${hotel} est-il populaire auprès des familles ?`,
        a: `Consulte “Équipements” (chambres familiales, etc.) et “Règles” (enfants et lits).`
      },
    ];
  }, [business?.name, business?.region, hotelDetails]);

  const travelerQuestions = useMemo(() => {
    const fromDb = hotelDetails?.traveler_questions || [];
    if (fromDb.length) return fromDb;
    return defaultFaq.slice(0, 6).map(x => ({ q: x.q, a: x.a }));
  }, [hotelDetails, defaultFaq]);

  const faqItems = useMemo(() => {
    const fromLegacyFaq = Array.isArray(hotelDetails?.faq) ? hotelDetails.faq : [];
    if (fromLegacyFaq.length) return fromLegacyFaq;
    return travelerQuestions;
  }, [hotelDetails, travelerQuestions]);

  const amenities = useMemo(() => {
    const fromDb = hotelDetails?.amenities;

    if (Array.isArray(fromDb)) {
      return { 'Équipements': fromDb } as Record<string, string[]>;
    }

    if (fromDb && Object.keys(fromDb).length) return fromDb as Record<string, string[]>;

    return {
      "Ses points forts": [
        "Connexion Wi-Fi gratuite",
        "Chambres non-fumeurs",
        "Réception (selon disponibilité)",
      ],
      "Général": ["Ascenseur", "Chambres familiales", "Climatisation (si applicable)"],
      "Sécurité": ["Détecteurs de fumée", "Coffre-fort (si applicable)"],
      "Internet": ["Wi-Fi gratuit dans tout l’établissement"],
      "Salle de bains": ["Serviettes", "Papier toilette", "Articles de toilette"],
    } as Record<string, string[]>;
  }, [hotelDetails]);

  const amenityList = useMemo(
    () => Object.entries(amenities as Record<string, string[]>).flatMap(([group, items]) =>
      (items as string[]).map((item) => ({ group, item }))
    ),
    [amenities]
  );

  const surroundings = useMemo(() => {
    const items = hotelDetails?.surroundings?.items || [];
    if (items.length) return hotelDetails?.surroundings;

    return {
      items: [],
      notes: ''
    };
  }, [hotelDetails]);

  const dining = useMemo(() => {
    return hotelDetails?.dining || { has_restaurant: false, restaurants: [] };
  }, [hotelDetails]);

  const house = useMemo(() => {
    const legacy = hotelDetails?.rules || {};
    const modern = hotelDetails?.house_rules || {};

    return {
      check_in: modern.check_in || legacy.check_in || 'À confirmer',
      check_out: modern.check_out || legacy.check_out || 'À confirmer',
      cancellation: modern.cancellation || legacy.cancellation || '',
      deposit: modern.deposit || legacy.deposit || '',
      children: modern.children || legacy.children || '',
      quiet_hours: modern.quiet_hours || legacy.quiet_hours || '',
      other: modern.other || legacy.other || [],
      pets_allowed: typeof hotelDetails?.rules?.pets_allowed === 'boolean'
        ? hotelDetails.rules.pets_allowed
        : truthyText(modern.pets),
      smoking_allowed: typeof hotelDetails?.rules?.smoking_allowed === 'boolean'
        ? hotelDetails.rules.smoking_allowed
        : truthyText(modern.smoking),
      parties_allowed: typeof hotelDetails?.rules?.parties_allowed === 'boolean'
        ? hotelDetails.rules.parties_allowed
        : truthyText(modern.parties),
      pets_text: modern.pets || '',
      smoking_text: modern.smoking || '',
      parties_text: modern.parties || '',
    };
  }, [hotelDetails]);

  const isLikelyHotel = useMemo(() => {
    const cat = (business?.category || '').toLowerCase();
    return (
      rooms.length > 0 ||
      cat.includes('hotel') ||
      cat.includes('hôtel') ||
      cat.includes('hébergement') ||
      cat.includes('accommodation')
    );
  }, [business?.category, rooms.length]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-blue-600" size={40} />
    </div>
  );

  if (!business) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 bg-slate-50">
      <p className="mb-4 text-lg">Établissement introuvable.</p>
      <button
        onClick={() => navigate('/explorer-plus')}
        className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition"
      >
        Retour aux hébergements
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20 relative overflow-x-hidden">
      <Header />

      {/* --- DESIGN "WOW 2026" - ARRIÈRE-PLAN --- */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1200px] h-[600px] bg-gradient-to-br from-blue-100/40 via-purple-100/20 to-transparent blur-[120px] pointer-events-none z-0"></div>

      <div className="pt-24 px-4 max-w-7xl mx-auto relative z-10">

        {/* --- BOUTON RETOUR --- */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors mb-6 group w-fit"
        >
          <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center group-hover:border-blue-200 group-hover:bg-blue-50 transition-all shadow-sm">
            <ArrowLeft size={16} />
          </div>
          Retour à l'écran précédent
        </button>

        <div className="mb-6 animate-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <span className="hover:underline cursor-pointer" onClick={() => navigate('/explorer-plus')}>Explorer</span>
            <span>/</span>
            <span className="hover:underline cursor-pointer">{business.region || 'Québec'}</span>
          </div>
          <div className="flex justify-between items-start gap-3">
            <h1 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-2">{business.name}</h1>
            <div className="flex gap-2 shrink-0">
              <button onClick={handleShare} className="p-3 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition shadow-sm">
                <Share size={20} />
              </button>
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-3 rounded-full border transition shadow-sm ${isFavorite ? 'bg-red-50 border-red-100 text-red-500' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              >
                <Heart size={20} fill={isFavorite ? "currentColor" : "none"} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-600">
            <span className="flex items-center gap-1"><MapPin size={16} /> {business.city}, {business.region}</span>
            <span className="flex items-center gap-1"><Star size={16} className="fill-yellow-400 text-yellow-400" /> {averageRating ? averageRating.toFixed(1) : '5.0'} ({reviews.length} avis)</span>
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-black uppercase">Partenaire Certifié</span>
            {business.prp_id && (
              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-black uppercase">Soutenu par un Directeur Régional</span>
            )}
            <button
              type="button"
              onClick={openForfyPlanner}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all"
            >
              <Sparkles size={14} className="text-blue-100" />
              Forfy AI planifie ta journée
            </button>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[320px] md:h-[500px] rounded-3xl overflow-hidden shadow-2xl relative group cursor-pointer"
          onClick={() => setShowGallery(true)}
        >
          <div className="md:col-span-2 h-full relative">
            <img
              src={allImages[0] || "https://via.placeholder.com/800"}
              className="w-full h-full object-cover hover:scale-105 transition duration-700"
              alt=""
            />
          </div>
          <div className="hidden md:grid grid-rows-2 gap-2 h-full">
            <img src={allImages[1] || allImages[0]} className="w-full h-full object-cover hover:scale-105 transition duration-700" alt="" />
            <img src={allImages[2] || allImages[0]} className="w-full h-full object-cover hover:scale-105 transition duration-700" alt="" />
          </div>
          <div className="hidden md:grid grid-rows-2 gap-2 h-full relative">
            <img src={allImages[3] || allImages[0]} className="w-full h-full object-cover hover:scale-105 transition duration-700" alt="" />
            <div className="relative h-full overflow-hidden">
              <img src={allImages[4] || allImages[0]} className="w-full h-full object-cover hover:scale-105 transition duration-700" alt="" />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center hover:bg-black/50 transition">
                <span className="text-white font-bold flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full border border-white/30">
                  <ImageIcon size={16} /> Voir photos
                </span>
              </div>
            </div>
          </div>
        </div>

        {isForfeoReserveVirtualTourEnabled() && (
          <div className="mt-8">
            <ReserveVirtualTourViewer
              businessId={business.id}
              businessName={business.name}
            />
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12 relative z-10">

        <div className="lg:col-span-2 space-y-10 animate-in slide-in-from-left-8 duration-700">

          <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-sm border border-white/60">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                 <Sparkles className="text-purple-600" size={20}/>
              </div>
              <h2 className="text-2xl font-black text-slate-900">L'expérience</h2>
            </div>
            <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-line font-medium">
              {business.description || "Aucune description disponible pour cet établissement."}
            </p>

            {amenityList.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-100">
                <h3 className="text-sm font-black uppercase text-slate-400 tracking-widest mb-6">Ce que propose ce lieu</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(amenities as Record<string, string[]>).map(([group, items]) => (
                    <div key={group} className="rounded-3xl border border-slate-100 bg-slate-50/60 p-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-4">{group}</p>
                      <div className="space-y-3">
                        {(items as string[]).map((amenity, i) => (
                          <div key={`${group}-${i}`} className="flex items-center gap-3 text-slate-700 font-bold">
                            <div className="text-blue-500 shrink-0">{getAmenityIcon(amenity)}</div>
                            <span>{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {hotelDetails && (
            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-sm border border-white/60">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="text-green-600" size={20}/>
                </div>
                <h2 className="text-2xl font-black text-slate-900">Règles de l'hébergement</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-5 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
                    <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Arrivée</span>
                    <span className="font-black text-slate-900 flex items-center gap-2 text-lg"><Clock size={18} className="text-blue-500"/> {house.check_in}</span>
                  </div>
                  <div className="flex items-center justify-between p-5 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
                    <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Départ</span>
                    <span className="font-black text-slate-900 flex items-center gap-2 text-lg"><Clock size={18} className="text-orange-500"/> {house.check_out}</span>
                  </div>
                </div>
                <div className="space-y-5 flex flex-col justify-center">
                  <div className={`flex items-center gap-4 text-lg ${house.pets_allowed ? 'text-green-600' : 'text-slate-500'}`}>
                    {house.pets_allowed ? <CheckCircle size={28} className="fill-green-50"/> : <X size={28} className="text-red-400"/>}
                    <span className="font-bold">Animaux {house.pets_allowed ? 'autorisés' : 'non autorisés'}</span>
                  </div>
                  <div className={`flex items-center gap-4 text-lg ${house.smoking_allowed ? 'text-green-600' : 'text-slate-500'}`}>
                    {house.smoking_allowed ? <CheckCircle size={28} className="fill-green-50"/> : <X size={28} className="text-red-400"/>}
                    <span className="font-bold">Fumeurs {house.smoking_allowed ? 'autorisés' : 'interdits'}</span>
                  </div>
                  <div className={`flex items-center gap-4 text-lg ${house.parties_allowed ? 'text-green-600' : 'text-slate-500'}`}>
                    {house.parties_allowed ? <CheckCircle size={28} className="fill-green-50"/> : <X size={28} className="text-red-400"/>}
                    <span className="font-bold">Fêtes {house.parties_allowed ? 'autorisées' : 'interdites'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {posts.length > 0 && (
            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-sm border border-white/60">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                  <MessageSquareText className="text-pink-500" size={28}/> Actu du Réseau
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {posts.map(post => (
                  <div key={post.id} className="bg-slate-50/50 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col md:flex-row">
                    {post.image_url && (
                      <div className="w-full md:w-48 h-48 md:h-auto overflow-hidden">
                        <img src={post.image_url} className="w-full h-full object-cover hover:scale-105 transition duration-700"/>
                      </div>
                    )}
                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <p className="text-slate-700 font-medium line-clamp-3 mb-4">"{post.content}"</p>
                      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                        <div className="flex items-center gap-4">
                          <button onClick={() => handleLikePost(post.id)} className={`flex items-center gap-1.5 text-xs font-black uppercase transition ${likedPosts.has(post.id) ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}>
                            <Heart size={16} fill={likedPosts.has(post.id) ? "currentColor" : "none"}/> {post.likes_count || 0}
                          </button>
                          <div className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-400">
                            <MessageSquare size={16}/> {post.comments?.length || 0}
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{new Date(post.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rooms.length > 0 && (
            <div id="rooms-section" className="pt-4">
              <h2 className="text-3xl font-black text-slate-900 mb-8 drop-shadow-sm">Nos Hébergements</h2>
              <div className="space-y-6">
                {rooms.map(room => (
                  <div 
                    key={room.id} 
                    onClick={() => selectAndScroll(room)}
                    className={`cursor-pointer group bg-white/90 backdrop-blur-md rounded-[2rem] border-2 transition-all duration-300 overflow-hidden flex flex-col md:flex-row ${selectedRoom?.id === room.id ? 'border-blue-500 shadow-xl shadow-blue-500/10' : 'border-white/50 shadow-sm hover:shadow-lg hover:border-blue-200'}`}
                  >
                    <div className="w-full md:w-72 h-56 md:h-auto relative overflow-hidden bg-slate-100">
                      <img src={room.images?.[0] || room.image_url || "https://via.placeholder.com/400"} className="w-full h-full object-cover group-hover:scale-110 transition duration-700 ease-out"/>
                      {selectedRoom?.id === room.id && (
                        <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest shadow-lg flex items-center gap-1 animate-in zoom-in duration-300">
                          <CheckCircle size={14}/> Sélectionné
                        </div>
                      )}
                    </div>
                    <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl font-black text-slate-900 mb-2">{room.name}</h3>
                        <p className="text-slate-500 font-medium leading-relaxed line-clamp-2">{room.description}</p>
                        <div className="flex flex-wrap gap-4 mt-4">
                          <span className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600"><Users size={14} className="text-blue-500"/> {room.capacity_adults} Pers. max</span>
                          <span className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600"><Coffee size={14} className="text-orange-400"/> Petit-déj inclus</span>
                        </div>
                      </div>
                      <div className="mt-6 flex items-end justify-between border-t border-slate-100 pt-4">
                        <span className="text-3xl font-black text-slate-900">{room.price_per_night}$ <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">/nuit</span></span>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${selectedRoom?.id === room.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600'}`}>
                           <ArrowRight size={18} className={selectedRoom?.id === room.id ? '' : 'group-hover:translate-x-1 transition-transform'}/>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {products.length > 0 && (
            <div className="pt-4">
              <h2 className="text-3xl font-black text-slate-900 mb-8 flex items-center gap-3 drop-shadow-sm"><ShoppingBag className="text-pink-500" size={32}/> Boutique Souvenirs</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {products.map(prod => (
                  <div key={prod.id} className="bg-white/80 backdrop-blur-xl rounded-[2rem] border border-white/60 p-4 shadow-sm hover:shadow-xl transition-all duration-300 group">
                    <div className="h-56 rounded-2xl overflow-hidden mb-5 relative bg-slate-100">
                      <img src={prod.image_url || "https://via.placeholder.com/200"} className="w-full h-full object-cover group-hover:scale-110 transition duration-700 ease-out"/>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(prod); }}
                        className="absolute bottom-4 right-4 bg-white/90 backdrop-blur text-slate-900 p-4 rounded-2xl shadow-lg hover:bg-blue-600 hover:text-white transition-all transform active:scale-95 group-hover:-translate-y-1"
                      >
                        <Plus size={20} className="stroke-[3px]"/>
                      </button>
                    </div>
                    <div className="px-2">
                        <h4 className="font-black text-slate-900 text-xl mb-1">{prod.name}</h4>
                        <p className="text-slate-500 text-sm mb-4 line-clamp-2 font-medium">{prod.description}</p>
                        <div className="flex justify-between items-center">
                        <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">{prod.price}$</p>
                        <p className="text-[10px] font-black text-green-600 uppercase tracking-widest bg-green-50 px-2 py-1 rounded">En stock</p>
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {faqItems.length > 0 && (
            <div className="bg-white/80 backdrop-blur-xl p-8 md:p-10 rounded-[2.5rem] border border-white/60 shadow-sm pt-8">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
                 <h2 className="text-3xl font-black text-slate-900 flex items-center gap-4 tracking-tight"><HelpCircle className="text-blue-500" size={32}/> Questions fréquentes</h2>
                 <button 
                   onClick={() => setIsChatOpen(true)}
                   className="bg-slate-900 text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-600 transition-colors shadow-lg flex items-center gap-3 shrink-0"
                 >
                   <MessageSquare size={18}/> Poser une question
                 </button>
              </div>
              <div className="space-y-4">
                {faqItems.map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    q={item.q}
                    a={item.a}
                    open={openFaq === idx}
                    onToggle={() => setOpenFaq(openFaq === idx ? null : idx)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="pt-8">
            <h2 className="text-3xl font-black text-slate-900 mb-8 flex items-center gap-3 drop-shadow-sm">
                <Star className="text-yellow-400 fill-yellow-400" size={32}/> Avis des voyageurs
            </h2>

            <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-sm border border-white/60 mb-10">
              <div className="flex flex-col gap-8 xl:grid xl:grid-cols-[minmax(0,1.05fr)_280px_minmax(0,1fr)] xl:items-center">
                <div className="xl:max-w-none">
                  <h3 className="font-black text-lg mb-3 flex items-center gap-2"><Smile className="text-blue-500"/> Partage ton expérience</h3>
                  <p className="text-slate-600 font-medium leading-relaxed">
                    Publie un avis pour aider les prochains voyageurs. Ton avis peut être marqué comme vérifié
                    si une réservation confirmée est trouvée.
                  </p>
                  <button
                    type="button"
                    onClick={openReviewModal}
                    className="mt-6 bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center gap-2"
                  >
                    <Send size={18}/> Laisser un avis
                  </button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-6 items-center">
                  <div className="min-h-[220px] bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_10px_40px_-24px_rgba(15,23,42,0.22)] flex flex-col items-center justify-center text-center">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Note moyenne</p>
                    <div className="leading-none text-5xl font-black text-slate-900 tabular-nums">{averageRating ? averageRating.toFixed(1) : '—'}</div>
                    <div className="mt-4 flex items-center justify-center gap-1 text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={18} className={i < Math.round((averageRating || 0) / 2) ? 'fill-yellow-400' : 'text-slate-200'} />
                      ))}
                    </div>
                    <p className="mt-3 text-sm font-black text-slate-700">{averageRating ? scoreLabel(averageRating) : 'Aucun avis'}</p>
                    <p className="mt-1 text-xs font-bold text-slate-400">{reviews.length} avis</p>
                  </div>

                  <div className="space-y-4">
                    <BarRow label="Personnel" value={categoryAverages.personnel} />
                    <BarRow label="Équipements" value={categoryAverages.equipements} />
                    <BarRow label="Propreté" value={categoryAverages.proprete} />
                    <BarRow label="Confort" value={categoryAverages.confort} />
                    <BarRow label="Rapport Q/P" value={categoryAverages.qp} />
                    <BarRow label="Emplacement" value={categoryAverages.geo} />
                    <BarRow label="Wi‑Fi" value={categoryAverages.wifi} />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
                {reviewsLoading ? (
                    <div className="text-center py-12 bg-slate-50 rounded-[2.5rem] border border-slate-100">
                        <Loader2 className="mx-auto animate-spin text-blue-500 mb-4" size={34} />
                        <p className="text-slate-500 font-bold text-lg">Chargement des avis...</p>
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                        <Star className="mx-auto text-slate-300 mb-4" size={48}/>
                        <p className="text-slate-500 font-bold text-lg">Soyez le premier à donner votre avis !</p>
                    </div>
                ) : (
                    reviews.map((review) => {
                      const reviewerName = getReviewerName(review);
                      const reviewerInitial = reviewerName.charAt(0).toUpperCase();
                      const reviewStars = getReviewStars(review);
                      const reviewScore = normalizeRating(review);

                      return (
                        <div key={review.id} className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-black text-lg">
                                        {reviewerInitial}
                                    </div>
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="font-black text-slate-900">{reviewerName}</p>
                                          {review.is_verified ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-green-700 border border-green-100">
                                              <ShieldCheck size={12} /> Séjour vérifié
                                            </span>
                                          ) : null}
                                        </div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                          {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'Date inconnue'}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                  <div className="flex justify-end text-yellow-400">
                                      {[...Array(5)].map((_, i) => (
                                          <Star key={i} size={16} className={i < reviewStars ? 'fill-yellow-400' : 'text-slate-200'}/>
                                      ))}
                                  </div>
                                  <p className="mt-1 text-xs font-black uppercase tracking-widest text-slate-400">
                                    {reviewScore ? `${reviewScore.toFixed(1)}/10` : 'Sans note'}
                                  </p>
                                </div>
                            </div>
                            {review.title ? <p className="font-black text-slate-900 text-lg mb-2">{review.title}</p> : null}
                            <p className="text-slate-600 font-medium leading-relaxed italic">"{review.comment || 'Aucun commentaire'}"</p>
                            {(review.pros || review.cons) ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                                {review.pros ? (
                                  <div className="rounded-2xl bg-green-50 border border-green-100 p-4">
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-green-700 mb-2">Points forts</p>
                                    <p className="text-sm font-bold text-slate-700">{review.pros}</p>
                                  </div>
                                ) : null}
                                {review.cons ? (
                                  <div className="rounded-2xl bg-orange-50 border border-orange-100 p-4">
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-orange-700 mb-2">Points faibles</p>
                                    <p className="text-sm font-bold text-slate-700">{review.cons}</p>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                        </div>
                      );
                    })
                )}
            </div>
          </div>

        </div>

        {/* DROITE : WIDGET RÉSERVATION (STICKY) */}
        <div className="relative">
          <div id="booking-panel" className="sticky top-28 bg-white/90 backdrop-blur-2xl p-8 rounded-[2.5rem] shadow-2xl shadow-blue-900/5 border border-white animate-in fade-in slide-in-from-right-8 duration-700 transition-all">
            <div className="flex justify-between items-end mb-8 border-b border-slate-100 pb-6">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">Tarif indicatif</p>
                <div className="text-5xl font-black text-slate-900">
                  {totalPrice > 0 ? totalPrice : (selectedRoom?.price_per_night || 0)} <span className="text-2xl align-top text-slate-400">$</span>
                </div>
                <p className="mt-2 max-w-xs text-xs font-semibold text-slate-400">Le prix officiel, les taxes et les frais sont résolus côté serveur par Forfeo.com.</p>
              </div>
              <div className="text-right flex flex-col items-end">
                <div className="flex items-center gap-1 text-yellow-500 font-black text-lg bg-yellow-50 px-3 py-1 rounded-xl"><Star size={16} className="fill-yellow-500"/> {averageRating ? averageRating.toFixed(1) : '5.0'}</div>
                <p className="text-xs font-bold text-slate-400 mt-2">{reviews.length} avis vérifiés</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 focus-within:ring-4 ring-blue-500/20 focus-within:border-blue-500 transition-all">
                  <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Arrivée</label>
                  <input type="date" min={todayStr} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-transparent font-black text-slate-900 outline-none cursor-pointer"/>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 focus-within:ring-4 ring-blue-500/20 focus-within:border-blue-500 transition-all">
                  <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Départ</label>
                  <input type="date" min={checkIn || todayStr} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent font-black text-slate-900 outline-none cursor-pointer"/>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 focus-within:ring-4 ring-blue-500/20 focus-within:border-blue-500 transition-all">
                <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Voyageurs</label>
                <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value))} className="w-full bg-transparent font-black text-slate-900 outline-none cursor-pointer">
                  <option value="1">1 Voyageur</option>
                  <option value="2">2 Voyageurs</option>
                  <option value="3">3 Voyageurs</option>
                  <option value="4">4+ Voyageurs</option>
                </select>
              </div>
            </div>

            {!reserveUserExperienceEnabled && checkIn && checkOut && availabilityMessage && (
              <div className={`p-4 rounded-2xl text-xs font-black uppercase tracking-widest mb-6 flex items-center justify-center gap-2 ${isDateAvailable ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-700'}`}>
                {isDateAvailable ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                {availabilityMessage}
              </div>
            )}

            <button 
              onClick={handleBooking}
              disabled={bookingLoading || !business}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] shadow-2xl shadow-blue-500/40 hover:shadow-blue-500/60 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-sm"
            >
              {bookingLoading ? <Loader2 className="animate-spin" size={24} /> : <ArrowRight size={20} />}
              {bookingLoading ? "Ouverture..." : reserveUserExperienceEnabled ? "Ouvrir Forfeo Reserve" : "Voir les options de réservation"}
            </button>

            <button 
              onClick={() => setIsChatOpen(true)}
              className="w-full mt-4 bg-slate-50 border-2 border-slate-100 text-slate-600 py-4 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-100 hover:text-blue-600 transition shadow-sm flex items-center justify-center gap-2 text-xs"
            >
               <MessageSquare size={16} /> Contacter l'établissement
            </button>

            {isLikelyHotel && (
              <button
                type="button"
                onClick={() => setAroundOpen(true)}
                className="mt-4 w-full bg-white border border-slate-200 text-slate-900 py-4 rounded-[2rem] font-black uppercase tracking-widest text-xs hover:bg-slate-50 transition flex items-center justify-center gap-2"
              >
                <Sparkles size={16} /> Autour de moi (IA)
              </button>
            )}

            <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-6 flex items-center justify-center gap-2">
              <ShieldCheck size={14} className="text-green-500" /> Disponibilité Reserve • Prix confirmé côté serveur
            </p>

            {geoErr ? (
              <div className="mt-4 bg-red-50 border border-red-100 text-red-700 rounded-2xl p-4 text-xs font-bold text-center">
                {geoErr}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showGallery && (
        <div className="fixed inset-0 z-[120] bg-slate-900/98 backdrop-blur-3xl flex flex-col animate-in fade-in duration-500">
          <div className="p-6 md:p-10 flex justify-end">
            <button onClick={() => setShowGallery(false)} className="text-white bg-white/10 p-4 rounded-full hover:bg-red-500 hover:rotate-90 transition-all duration-300">
              <X size={24} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 md:p-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
            {allImages.map((src, idx) => (
              <img key={idx} src={src} className="w-full rounded-3xl shadow-2xl hover:scale-[1.02] transition duration-700" alt="" />
            ))}
          </div>
        </div>
      )}

      {/* ✅ MODALE "AUTOUR DE MOI" */}
      {aroundOpen && (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-6">
          <div className="absolute inset-0" onClick={() => !geoLoading && setAroundOpen(false)} />
          <div className="relative w-full md:max-w-2xl bg-white rounded-t-[3rem] md:rounded-[3rem] shadow-2xl border border-slate-100 max-h-[90vh] md:max-h-[85vh] flex flex-col animate-in slide-in-from-bottom-8 duration-500">
            <div className="p-6 md:p-8 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Autour de moi</p>
                <h3 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-3 tracking-tight">
                  <Sparkles className="text-blue-600" size={28}/> Idées rapides
                </h3>
              </div>
              <button
                onClick={() => !geoLoading && setAroundOpen(false)}
                className="shrink-0 p-3 bg-slate-50 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
              >
                <X size={20}/>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  type="button"
                  onClick={openDirectionsFromMe}
                  className="bg-slate-900 text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 transition-colors flex items-center gap-2 shadow-lg"
                  disabled={geoLoading}
                >
                  {geoLoading ? <Loader2 className="animate-spin" size={18} /> : <MapPin size={18} />}
                  {geoLoading ? "Localisation…" : "Itinéraire depuis moi"}
                </button>
                {placeQuery ? (
                  <a
                    href={googleMapsSearchUrl(placeQuery)}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-white border border-slate-200 text-slate-700 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-colors flex items-center gap-2 shadow-sm"
                  >
                    Ouvrir sur Maps <ExternalLink size={16} />
                  </a>
                ) : null}
              </div>
              {geoErr ? (
                <div className="mb-6 bg-red-50 border border-red-100 text-red-700 rounded-2xl p-4 text-xs font-bold flex items-center gap-2">
                  <AlertTriangle size={16}/> {geoErr}
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aroundSuggestions.map((s) => (
                  <a
                    key={s.label}
                    href={googleMapsSearchUrl(s.q)}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-slate-100 bg-slate-50 rounded-2xl p-5 hover:bg-white hover:shadow-md hover:border-blue-200 transition-all duration-300"
                  >
                    <div className="font-black text-slate-900 flex items-center gap-2 text-base">
                      <Sparkles size={16} className="text-blue-500" /> {s.label}
                    </div>
                    <div className="text-xs font-bold text-slate-400 mt-2 line-clamp-1">{s.q}</div>
                  </a>
                ))}
              </div>

              <button 
                onClick={openForfyPlanner}
                className="w-full mt-8 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white p-5 rounded-2xl flex items-center justify-center gap-3 font-black uppercase tracking-[0.2em] text-xs hover:shadow-xl hover:shadow-blue-500/40 transition-all transform active:scale-95"
              >
                <Sparkles size={20} className="text-blue-100" />
                Itinéraire intelligent avec Forfy IA
              </button>
              <p className="mt-3 text-center text-[11px] font-bold text-slate-400">
                Forfy prépare ton parcours dans la page, avec route IA sécurisée dès qu’un endpoint OpenAI est branché.
              </p>

            </div>
          </div>
        </div>
      )}

      {showItineraryPlanner && (
        <div className="fixed inset-0 z-[118] bg-slate-950/70 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-5">
          <div className="absolute inset-0" onClick={() => !itineraryLoading && setShowItineraryPlanner(false)} />
          <div className="relative w-full h-[92vh] md:h-[88vh] md:max-w-7xl bg-white rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl border border-white/60 overflow-hidden animate-in slide-in-from-bottom-6 duration-500">
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-r from-blue-600/20 via-indigo-500/15 to-violet-500/20 pointer-events-none" />
            <div className="relative h-full grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)]">
              <div className="border-b xl:border-b-0 xl:border-r border-slate-100 bg-white/90 backdrop-blur-xl flex flex-col min-h-0">
                <div className="p-6 md:p-7 border-b border-slate-100">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400 mb-2">Forfy AI</p>
                      <h3 className="text-2xl md:text-3xl font-black text-slate-900 leading-tight">
                        Itinéraire intelligent
                      </h3>
                      <p className="mt-3 text-sm font-medium text-slate-500">
                        Forfy construit un parcours autour de {business?.name} selon ton style, ton budget et ton rythme.
                      </p>
                    </div>
                    <button
                      onClick={() => !itineraryLoading && setShowItineraryPlanner(false)}
                      className="shrink-0 p-3 bg-slate-50 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] ${
                      itinerarySource === 'openai'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        : itinerarySource === 'fallback'
                          ? 'bg-amber-50 text-amber-700 border border-amber-100'
                          : 'bg-slate-900 text-white'
                    }`}>
                      <Sparkles size={12} />
                      {itinerarySource === 'openai'
                        ? 'Mode OpenAI actif'
                        : itinerarySource === 'fallback'
                          ? 'Mode Forfy secours'
                          : 'Mode intelligent Forfy'}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] border border-blue-100">
                      <MapPin size={12} />
                      {business?.city}, {business?.region}
                    </span>
                    {itineraryModelName ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-white text-slate-700 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] border border-slate-200">
                        Modèle {itineraryModelName}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-5">
                  <div className="rounded-[2rem] border border-slate-100 bg-slate-50 p-5">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Ambiance</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ['discovery', 'Découverte'],
                        ['foodie', 'Gourmand'],
                        ['relax', 'Détente'],
                        ['family', 'Famille'],
                        ['romance', 'Romance'],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setItineraryStyle(value as any)}
                          className={`rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest transition ${itineraryStyle === value ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-200 hover:text-blue-600'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                    <div className="rounded-[1.75rem] border border-slate-100 bg-slate-50 p-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Moment</p>
                      <select
                        value={itineraryMoment}
                        onChange={(e) => setItineraryMoment(e.target.value as any)}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-black text-slate-900 outline-none"
                      >
                        <option value="full">Journée complète</option>
                        <option value="morning">Matin</option>
                        <option value="afternoon">Après-midi</option>
                        <option value="evening">Soirée</option>
                      </select>
                    </div>
                    <div className="rounded-[1.75rem] border border-slate-100 bg-slate-50 p-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Budget</p>
                      <select
                        value={itineraryBudget}
                        onChange={(e) => setItineraryBudget(e.target.value as any)}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-black text-slate-900 outline-none"
                      >
                        <option value="soft">Léger</option>
                        <option value="medium">Équilibré</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                    <div className="rounded-[1.75rem] border border-slate-100 bg-slate-50 p-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Déplacements</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setItineraryTravelMode('drive')}
                          className={`rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest transition ${itineraryTravelMode === 'drive' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-200 hover:text-blue-600'}`}
                        >
                          Voiture
                        </button>
                        <button
                          type="button"
                          onClick={() => setItineraryTravelMode('walk')}
                          className={`rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest transition ${itineraryTravelMode === 'walk' ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-200 hover:text-blue-600'}`}
                        >
                          À pied
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={generateForfyItinerary}
                    disabled={itineraryLoading}
                    className="w-full rounded-[1.75rem] bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-6 py-5 text-white font-black uppercase tracking-[0.2em] text-xs shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all disabled:opacity-60 flex items-center justify-center gap-3"
                  >
                    {itineraryLoading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                    {itineraryLoading ? 'Génération en cours...' : 'Regénérer le plan Forfy'}
                  </button>

                  <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-3">Accès rapide</p>
                    <div className="space-y-2">
                      {placeQuery ? (
                        <a
                          href={googleMapsSearchUrl(placeQuery)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:border-blue-200 hover:text-blue-600 transition"
                        >
                          Ouvrir le lieu sur Maps
                          <ExternalLink size={16} />
                        </a>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setShowItineraryPlanner(false);
                          setIsChatOpen(true);
                        }}
                        className="w-full flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:border-blue-200 hover:text-blue-600 transition"
                      >
                        Continuer avec le chat Forfy
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>

                  {itineraryError ? (
                    <div className="rounded-[1.75rem] border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">
                      {itineraryError}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="min-h-0 bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.15),_transparent_35%),linear-gradient(to_bottom,_#ffffff,_#f8fafc)] flex flex-col">
                <div className="px-6 md:px-8 pt-6 md:pt-8 pb-4 border-b border-slate-100 bg-white/70 backdrop-blur-xl">
                  <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400 mb-2">Plan du moment</p>
                      <h4 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight">
                        {itineraryPlan?.title || `Journée smart autour de ${business?.name}`}
                      </h4>
                      <p className="mt-3 max-w-3xl text-sm md:text-base font-medium text-slate-500">
                        {itineraryPlan?.intro || `Forfy prépare un parcours dynamique autour de ${business?.name}. Choisis un mood et laisse l’assistant te proposer une boucle pratique.`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(itineraryPlan?.bestFor || []).slice(0, 4).map((tag, idx) => (
                        <span key={idx} className="rounded-full bg-white border border-slate-200 px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 md:py-8 space-y-6">
                  {itineraryError ? (
                    <div className="rounded-[2rem] border border-red-100 bg-red-50/90 p-5 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-2">Erreur Forfy AI</p>
                      <p className="text-sm font-bold text-red-700">{itineraryError}</p>
                    </div>
                  ) : null}

                  {itineraryWarning ? (
                    <div className={`rounded-[2rem] border p-5 shadow-sm ${
                      itinerarySource === 'openai'
                        ? 'border-emerald-100 bg-emerald-50/80'
                        : 'border-amber-100 bg-amber-50/85'
                    }`}>
                      <p className={`text-[10px] font-black uppercase tracking-[0.3em] mb-2 ${
                        itinerarySource === 'openai' ? 'text-emerald-600' : 'text-amber-600'
                      }`}>
                        {itinerarySource === 'openai' ? 'Signal moteur IA' : 'Information moteur'}
                      </p>
                      <p className={`text-sm font-bold ${
                        itinerarySource === 'openai' ? 'text-emerald-800' : 'text-amber-800'
                      }`}>
                        {itineraryWarning}
                      </p>
                    </div>
                  ) : null}

                  {itineraryEndpointLabel ? (
                    <div className="rounded-[2rem] border border-slate-100 bg-white p-4 shadow-sm">
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Route active</p>
                      <p className="text-sm font-black text-slate-700 break-all">{itineraryEndpointLabel}</p>
                    </div>
                  ) : null}

                  {itineraryLoading && !itineraryPlan ? (
                    <div className="rounded-[2rem] border border-slate-100 bg-white p-8 shadow-sm flex flex-col items-center justify-center text-center min-h-[300px]">
                      <div className="relative mb-5">
                        <div className="absolute inset-0 rounded-full bg-blue-500/15 blur-2xl" />
                        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-xl">
                          <Loader2 className="animate-spin" size={28} />
                        </div>
                      </div>
                      <p className="text-lg font-black text-slate-900">Forfy construit ton parcours...</p>
                      <p className="mt-2 max-w-md text-sm font-medium text-slate-500">
                        Analyse du lieu, des environs, du budget et du style demandé pour produire un itinéraire chic et utile.
                      </p>
                    </div>
                  ) : null}

                  {!itineraryLoading && itineraryPlan?.blocks?.length ? itineraryPlan.blocks.map((block, blockIdx) => (
                    <div key={`${block.title}-${blockIdx}`} className="rounded-[2rem] border border-white/70 bg-white/90 backdrop-blur-xl p-6 md:p-7 shadow-[0_20px_60px_-28px_rgba(15,23,42,0.25)]">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500 mb-2">{block.label}</p>
                          <h5 className="text-xl md:text-2xl font-black text-slate-900">{block.title}</h5>
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] w-fit">
                          <Clock size={14} />
                          {block.time || 'Flexible'}
                        </div>
                      </div>
                      {block.summary ? (
                        <p className="mt-4 text-sm md:text-base font-medium text-slate-500 max-w-3xl">{block.summary}</p>
                      ) : null}

                      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {block.stops.map((stop, stopIdx) => (
                          <div key={`${stop.title}-${stopIdx}`} className="rounded-[1.5rem] border border-slate-100 bg-slate-50/80 p-5">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 mb-2">Arrêt {stopIdx + 1}</p>
                                <h6 className="text-lg font-black text-slate-900">{stop.title}</h6>
                                {stop.subtitle ? <p className="mt-1 text-sm font-bold text-blue-600">{stop.subtitle}</p> : null}
                              </div>
                              {stop.duration ? (
                                <span className="shrink-0 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                                  {stop.duration}
                                </span>
                              ) : null}
                            </div>
                            {stop.reason ? <p className="mt-3 text-sm font-medium text-slate-500">{stop.reason}</p> : null}
                            {stop.mapsQuery ? (
                              <a
                                href={googleMapsSearchUrl(stop.mapsQuery)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-slate-700 hover:border-blue-200 hover:text-blue-600 transition"
                              >
                                Voir sur Maps
                                <ExternalLink size={14} />
                              </a>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )) : null}

                  {!itineraryLoading && itineraryPlan && !itineraryPlan.blocks.length ? (
                    <div className="rounded-[2rem] border border-slate-100 bg-white p-8 shadow-sm">
                      <p className="text-lg font-black text-slate-900">{itineraryPlan.intro || 'Forfy a généré un point de départ.'}</p>
                    </div>
                  ) : null}

                  {(itineraryPlan?.tips?.length || itineraryPlan?.disclaimers?.length) ? (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-4">Conseils pratiques</p>
                        <div className="space-y-3">
                          {(itineraryPlan?.tips || []).map((tip, idx) => (
                            <div key={idx} className="flex gap-3 text-sm font-medium text-slate-600">
                              <span className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-[2rem] border border-slate-100 bg-slate-900 text-white p-6 shadow-sm">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/50 mb-4">Notes Forfy</p>
                        <div className="space-y-3">
                          {(itineraryPlan?.disclaimers || []).map((note, idx) => (
                            <p key={idx} className="text-sm font-medium text-white/80">{note}</p>
                          ))}
                          {itinerarySource === 'fallback' ? (
                            <p className="text-sm font-medium text-white/80">
                              Le rendu reste 100 % dans ta page. Pour une vraie génération OpenAI, branche une route sécurisée comme <span className="font-black text-white">/functions/v1/forfy-itinerary</span> ou <span className="font-black text-white">/ai/forfy-itinerary</span>.
                            </p>
                          ) : null}
                          {itinerarySource === 'openai' && itineraryModelName ? (
                            <p className="text-sm font-medium text-white/80">
                              Génération serveur active via <span className="font-black text-white">{itineraryModelName}</span>.
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReviewModal && (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-6">
          <div className="absolute inset-0" onClick={() => !reviewSubmitting && setShowReviewModal(false)} />
          <div
            className="
              relative w-full md:max-w-3xl
              bg-white
              rounded-t-[3rem] md:rounded-[3rem]
              shadow-2xl border border-slate-100
              max-h-[90vh] md:max-h-[85vh]
              flex flex-col
              animate-in slide-in-from-bottom-8 duration-500
            "
          >
            <div className="p-6 md:p-8 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Votre avis</p>
                <h3 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-3 tracking-tight">
                  <ThumbsUp className="text-blue-600" /> Laissez une évaluation
                </h3>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {userHasConfirmedReservation ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-100">
                      <ShieldCheck size={14} /> Séjour vérifié (auto)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
                      <AlertTriangle size={14} /> Non vérifié (Aucune réservation trouvée)
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => !reviewSubmitting && setShowReviewModal(false)}
                className="shrink-0 p-3 bg-slate-50 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
              >
                <X size={20}/>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { label: 'Propreté (0-10)', value: rClean, set: setRClean },
                  { label: 'Confort (0-10)', value: rComfort, set: setRComfort },
                  { label: 'Emplacement (0-10)', value: rLocation, set: setRLocation },
                  { label: 'Rapport Q/P (0-10)', value: rValue, set: setRValue },
                ].map((f, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">{f.label}</p>
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={0.1}
                      value={f.value}
                      onChange={(e) => f.set(Number(e.target.value))}
                      className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 font-black text-slate-900 text-lg outline-none focus:ring-4 ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Titre de l'avis</p>
                <input
                  value={rTitle}
                  onChange={(e) => setRTitle(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 font-black text-slate-900 text-lg outline-none focus:ring-4 ring-blue-500/20 focus:bg-white transition-all"
                  placeholder="Ex: Séjour incroyable !"
                />
              </div>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Points forts</p>
                  <textarea
                    value={rPros}
                    onChange={(e) => setRPros(e.target.value)}
                    className="w-full min-h-[120px] bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 font-bold text-slate-700 outline-none focus:ring-4 ring-blue-500/20 focus:bg-white transition-all resize-none"
                    placeholder="Ex: La piscine chauffée, le lit king size..."
                  />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Points faibles</p>
                  <textarea
                    value={rCons}
                    onChange={(e) => setRCons(e.target.value)}
                    className="w-full min-h-[120px] bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 font-bold text-slate-700 outline-none focus:ring-4 ring-blue-500/20 focus:bg-white transition-all resize-none"
                    placeholder="Ex: Le bruit le matin, le parking payant..."
                  />
                </div>
              </div>
              <div className="mt-8">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Votre commentaire global</p>
                <textarea
                  value={rComment}
                  onChange={(e) => setRComment(e.target.value)}
                  className="w-full min-h-[160px] bg-slate-50 border border-slate-100 rounded-2xl px-6 py-5 font-bold text-slate-700 outline-none focus:ring-4 ring-blue-500/20 focus:bg-white transition-all resize-none"
                  placeholder="Racontez votre expérience aux futurs voyageurs..."
                />
              </div>
            </div>
            <div className="p-6 md:p-8 border-t border-slate-100 flex flex-col md:flex-row gap-4 md:items-center md:justify-between bg-slate-50 rounded-b-[3rem]">
              <button
                onClick={() => !reviewSubmitting && setShowReviewModal(false)}
                className="w-full md:w-auto bg-white text-slate-700 border border-slate-200 px-8 py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs hover:bg-slate-100 transition-colors"
                disabled={reviewSubmitting}
              >
                Annuler
              </button>
              <button
                onClick={submitReview}
                disabled={reviewSubmitting}
                className="w-full md:w-auto bg-slate-900 text-white px-10 py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs hover:bg-blue-600 transition-colors shadow-xl disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {reviewSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {reviewSubmitting ? "Envoi en cours..." : "Publier l’avis"}
              </button>
            </div>
          </div>
        </div>
      )}

      <button 
        onClick={() => setIsChatOpen(!isChatOpen)} 
        className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 bg-gradient-to-r from-blue-600 to-indigo-600 text-white w-14 h-14 md:w-16 md:h-16 rounded-full shadow-[0_18px_50px_-16px_rgba(37,99,235,0.75)] flex items-center justify-center hover:scale-110 hover:shadow-[0_24px_65px_-18px_rgba(79,70,229,0.75)] transition-all duration-300"
      >
        <MessageSquare size={24} className="md:w-7 md:h-7" />
      </button>

      {isChatOpen && (
         <div className="fixed inset-0 z-[10050] flex items-center justify-center overflow-hidden bg-slate-900/65 p-0 backdrop-blur-md lg:p-5">
            <div className="absolute inset-0" onClick={() => setIsChatOpen(false)}></div>
            <div className="relative z-10 flex h-[100dvh] w-full max-w-[1280px] flex-col overflow-hidden rounded-none border-0 bg-white shadow-2xl animate-in zoom-in-95 duration-300 lg:h-[90dvh] lg:rounded-[2rem] lg:border lg:border-white/60">
               <div className="relative shrink-0 overflow-hidden border-b border-slate-100 bg-slate-950 text-white">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.35),_transparent_35%),radial-gradient(circle_at_top_left,_rgba(99,102,241,0.25),_transparent_25%)]" />
                  <div className="relative flex items-center justify-between gap-2 px-3 py-2.5 lg:items-start lg:gap-3 lg:p-5">
                    <div className="min-w-0">
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 lg:mb-2 lg:gap-2">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-white/80">
                          <MessageSquare size={12} className="text-blue-300" />
                          Forfy en direct
                        </span>
                        <span className="hidden lg:inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-blue-200">
                          {business?.city}, {business?.region}
                        </span>
                      </div>
                      <h3 className="flex min-w-0 items-center gap-2 truncate text-lg font-black sm:text-xl lg:gap-3 lg:text-3xl">
                        {business?.name || "Support"}
                      </h3>
                      <p className="mt-2 hidden text-[11px] uppercase tracking-[0.20em] text-slate-300 lg:block lg:text-xs lg:tracking-[0.25em]">
                        Assistant Forfy • résolution large • conversation complète
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setIsChatOpen(false);
                          setShowItineraryPlanner(true);
                          if (!itineraryPlan && !itineraryLoading) {
                            generateForfyItinerary();
                          }
                        }}
                        className="hidden lg:inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-white hover:bg-white/15 transition"
                      >
                        <Sparkles size={14} className="text-blue-200" />
                        Planifier avec Forfy AI
                      </button>
                      <button onClick={() => setIsChatOpen(false)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 p-0 transition-colors hover:bg-white/20 lg:h-11 lg:w-11">
                        <X size={20}/>
                      </button>
                    </div>
                  </div>
               </div>
               <div className="flex-1 min-h-0 bg-slate-50">
                  <div className="h-full w-full overflow-hidden">
                    <ChatSystem userType="traveler" userId={currentUserId} initialContactId={business?.id || ""} />
                  </div>
               </div>
            </div>
         </div>
      )}

      <Footer />
    </div>
  );
}
